import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Table } from '@getsynapse/design-system';
import { ProjectDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import { SortingType } from 'utils/customTypes';
import { orderBy } from 'lodash';
import { PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS } from 'utils/constants/dashboard';
import ProjectDrillDownTableHeader from './ProjectDrillDownTableHeader/ProjectDrillDownTableHeader';
import ProjectDrillDownTableBody from './ProjectDrillDownTableBody/ProjectDrillDownTableBody';
import { useSelector } from 'react-redux';
import drilldownSelectors from 'state/normalized/insights/drilldownSelectors';
import DrillTableHeaderFilters from 'Pages/DashboardsDetailsPage/components/DrillTableHeaderFilters';
import { handleProjectExportData } from 'Pages/DashboardsDetailsPage/components/DrillDownSidePanel/drillDownDataExportHandlers';
import { insightDrilldownMetadataSelectors } from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import TableSkeletonLoader from 'Organisms/TableSkeletonLoader/TableSkeletonLoader';
import { selectUserRole } from 'state/User/userSlice';
import { USER_ROLES } from 'utils/constants';

const columnMap = new Map<string, string>([
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_NUMBER, 'project_number'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TITLE, 'title'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_STATUS, 'project_status'],
  [
    PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_PRIORITY,
    'project_priority',
  ],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_OWNER, 'owners'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_HEALTH, 'project_health'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINEES_UNITS, 'business_units'],
  [
    PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TOTAL_DEMAND_HOURS,
    'total_demand_hours',
  ],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CATEGORY_NAME, 'category_name'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS, 'programs'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.START_DATE, 'start_date'],
  [
    PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TARGET_COMPLETION_DATE,
    'target_completion_date',
  ],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.COMPLETION_DATE, 'completion_date'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.RESOURCING_TYPE, 'resourcing_type'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROCESS_NAME, 'process_name'],
  [PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STAGE_NAME, 'stage_name'],
]);

const ProjectDrillDownTable = () => {
  const items = useSelector(drilldownSelectors.selectActiveDrilldownProjects);
  const cardName = useSelector(
    drilldownSelectors.selectActiveDrilldownCardName
  );
  const userRole = useSelector(selectUserRole);
  const isLoading = useSelector(
    insightDrilldownMetadataSelectors.selectIsActiveDrilldownLoading
  );
  const exportItems = () => {
    handleProjectExportData(items);
  };
  const [orderByParam, setOrderByParam] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const orderByProjectTableData = (
    items: ProjectDrillDownItem[],
    column: string,
    order: SortingType
  ) => {
    if (!items || items.length === 0) return [];

    const fieldName = columnMap.get(column) || '';
    const finalData = orderBy(items, [fieldName], [order]);
    return finalData;
  };

  const sortedItems = useMemo(() => {
    return orderByProjectTableData(items, orderByParam, order);
  }, [items, orderByParam, order]);

  const handleSort = useCallback(
    (column: string) => {
      const isSameColumn = column === orderByParam;
      const newOrder = isSameColumn && order === 'asc' ? 'desc' : 'asc';
      setOrderByParam(column);
      setOrder(newOrder);
    },
    [orderByParam, order]
  );

  return (
    <div>
      <DrillTableHeaderFilters
        title={cardName}
        exportEnabled
        onExportClick={exportItems}
      />
      <div
        className={classnames(
          'w-full rounded-b overflow-auto border border-neutral-lighter-two max-h-filtered-table-body'
        )}
      >
        {isLoading && <TableSkeletonLoader />}
        {!isLoading && (
          <Table
            className={classnames('relative', {
              'max-w-full border-0 w-full': items.length,
              'w-full': !items.length,
            })}
            canSelectRows={false}
            data={{
              headData: {
                stickyHeader: true,
                handleSort,
                headCells: [],
              },
              rows: [],
              total: items.length,
            }}
            data-cy='team-dashboards-table'
          >
            <ProjectDrillDownTableHeader handleSort={handleSort} />
            <ProjectDrillDownTableBody
              items={sortedItems}
              readOnly={userRole !== USER_ROLES.ADMIN}
            />
          </Table>
        )}
      </div>
    </div>
  );
};

export default ProjectDrillDownTable;
